<template>
  <div class="cond-group-root">
    <template v-for="(child, index) in group.children" :key="child.id">
      <!-- 兄弟ノード間の区切り (2件目以降の前に挿入)。
                 親グループの operator に応じて「または」「かつ」を表示する。 -->
      <div
        v-if="index > 0"
        class="condition-separator"
      >
        <span>{{ group.operator === 'and' ? 'かつ' : 'または' }}</span>
      </div>
      <!-- ネストグループ -->
      <div v-if="child.type === 'group'" class="group-wrap">
        <div class="group-head">
          <button
            class="group-badge"
            :class="child.operator"
            type="button"
            @click="condActions.toggleOperator(child.id)"
          >
            <span class="op-icon">{{ child.operator === 'and' ? 'AND' : 'OR' }}</span>
            {{ child.operator === 'and' ? 'かつ' : 'または' }}
          </button>
          <span class="group-title">
            <strong>グループ条件</strong>
            <span class="group-title__count">{{ child.children.length }} 件</span>
          </span>
          <button
            class="icon-btn"
            type="button"
            title="グループを解除"
            @click="condActions.ungroup(child.id)"
          >⇱</button>
          <button
            class="icon-btn danger"
            type="button"
            title="グループを削除"
            @click="condActions.deleteNode(child.id)"
          ><svg
            class="icon-x"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
          ><use xlink:href="/assets/img/sprite.svg#close-thin" /></svg></button>
        </div>
        <div class="group-body">
          <condition-group-node
            :group="child"
            :is-root="false"
            :context-root="contextRoot"
          />
        </div>
      </div>

      <!-- 条件アイテム -->
      <div
        v-else
        class="condition-item"
        :class="{ 'empty-state': isRoot && group.children.length === 1 && !child.url, 'highlighted': condActions.isHovered(child.id) }"
        :data-id="child.id"
        @mouseenter="condActions.setHovered(child.id)"
        @mouseleave="condActions.setHovered(null)"
      >
        <!-- schedule/unschedule/suppress ルートでは最後の 1 件も削除可能にし、
                     空グループ (empty state) へ戻せるようにする。
                     クリアボタン系と同様のニュートラルな hover 表現にしたいので
                     danger 修飾は付けず .icon-btn のデフォルト hover を使う。
                     配置は CSS 側で .condition-item > .icon-btn を absolute に流し込み、
                     カード右上の角に半分被せる (top:-14px / right:-14px)。
                     通常時は opacity:0 / pointer-events:none で隠し、
                     .condition-item への hover / focus-within で表示に切り替える。 -->
        <button
          class="icon-btn"
          type="button"
          :disabled="group.children.length === 1 && !isRoot"
          title="削除"
          @click="condActions.deleteNode(child.id)"
        ><svg
          class="icon-x"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
        ><use xlink:href="/assets/img/sprite.svg#close-thin" /></svg></button>
        <div class="cond-row">
          <div class="input-wrap" :class="{ 'has-error': !!errorOf(child.id, 'url') }">
            <input
              class="input-field"
              type="text"
              :value="child.url"
              placeholder="例: https://example.com/cart, /product/"
              @input="condActions.updateUrl(child.id, $event.target.value)"
            />
            <error-balloon v-if="errorOf(child.id, 'url')" :top="-34" :nowrap="true">{{ errorOf(child.id, 'url') }}</error-balloon>
          </div>
          <select-text
            :classes="['u-w90']"
            :value="child.method"
            :elements="methodElements"
            @select="condActions.setMethod(child.id, $event)"
          />
        </div>

        <!-- 設定後の例示 (URL は eq/contains のみ提示。notEq/notContains の例は当面非表示) -->
        <div v-if="child._methodJustSelected" class="method-example">
          <template v-if="child.method === 'eq'">
            一致: <span class="match">{{ urlDisplay(child) }}</span>
            不一致: <span class="nomatch">{{ urlDisplay(child) }}/detail</span>
          </template>
          <!--
                    <template v-else-if="child.method === 'notEq'">
                        一致: <span class="match">{{ urlDisplay(child) }}/detail</span>
                        <span class="match">/products</span>
                        不一致: <span class="nomatch">{{ urlDisplay(child) }}</span>
                    </template>
                    -->
          <template v-else-if="child.method === 'contains'">
            一致: <span class="match">{{ urlDisplay(child) }}</span>
            <span class="match">/my-{{ urlTail(child) }}</span>
            <span class="match">/foo{{ urlDisplay(child) }}/bar</span>
          </template>
          <!--
                    <template v-else-if="child.method === 'notContains'">
                        一致: <span class="match">/products</span>
                        不一致: <span class="nomatch">{{ urlDisplay(child) }}</span>
                        <span class="nomatch">/foo{{ urlDisplay(child) }}/bar</span>
                    </template>
                    -->
        </div>

        <!-- クエリストリング -->
        <div class="query-section">
          <div class="query-toggle-row">
            <button
              class="refinement-toggle"
              :class="{ 'open': child.queryOpen }"
              type="button"
              @click="condActions.toggleQuery(child.id)"
            >
              <span>+</span>
              クエリストリングで絞り込む
              <span class="caret">{{ child.queryOpen ? '▲' : '▼' }}</span>
            </button>
            <span class="optional-badge">任意</span>
          </div>
          <div v-if="child.queryOpen" class="query-params-list">
            <div class="query-params-note">以下のすべてを満たす</div>
            <div
              v-for="qp in child.queryParams"
              :key="qp.id"
              class="query-param-row"
            >
              <div class="input-wrap" :class="{ 'has-error': !!errorOf(child.id, 'query.' + qp.id + '.key') }">
                <input
                  class="input-field"
                  type="text"
                  :value="qp.key"
                  placeholder="キー (例: utm_source)"
                  @input="condActions.updateQueryKey(child.id, qp.id, $event.target.value)"
                />
                <error-balloon v-if="errorOf(child.id, 'query.' + qp.id + '.key')" :top="-34" :nowrap="true">{{ errorOf(child.id, 'query.' + qp.id + '.key') }}</error-balloon>
              </div>
              <div class="input-wrap" :class="{ 'has-error': !!errorOf(child.id, 'query.' + qp.id + '.value') }">
                <input
                  class="input-field"
                  type="text"
                  :value="qp.value"
                  placeholder="値 (例: mail)"
                  @input="condActions.updateQueryValue(child.id, qp.id, $event.target.value)"
                />
                <error-balloon v-if="errorOf(child.id, 'query.' + qp.id + '.value')" :top="-34" :nowrap="true">{{ errorOf(child.id, 'query.' + qp.id + '.value') }}</error-balloon>
              </div>
              <div class="select-wrap">
                <select
                  class="select-field"
                  :value="qp.method"
                  @change="condActions.updateQueryMethod(child.id, qp.id, $event.target.value)"
                >
                  <option
                    v-for="opt in queryMethodOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >{{ opt.label }}</option>
                </select>
              </div>
              <!-- クリアボタン系と同じ落ち着いた hover にするため danger 修飾は付けない。 -->
              <button
                class="icon-btn"
                type="button"
                :title="child.queryParams.length === 1 ? 'クリアして閉じる' : '削除'"
                @click="condActions.deleteQuery(child.id, qp.id)"
              ><svg
                class="icon-x"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
              ><use xlink:href="/assets/img/sprite.svg#close-thin" /></svg></button>
            </div>
            <button
              class="query-add-btn"
              type="button"
              @click="condActions.addQuery(child.id)"
            >クエリストリングを追加する</button>
          </div>
        </div>

        <!-- ユーザー行動の条件（現状 UI 非表示。値は _buildPatternFromUrlNode / _convertPatternToUrlNode でも無視される） -->
        <div v-if="false" class="refinement-row">
          <div class="refinement-toggle-row">
            <button
              class="refinement-toggle"
              :class="{ 'open': child.refineOpen }"
              type="button"
              @click="condActions.toggleRefine(child.id)"
            >
              <span>+</span>
              ユーザー行動の条件でさらに絞り込む
              <span class="caret">{{ child.refineOpen ? '▲' : '▼' }}</span>
            </button>
            <span class="optional-badge">任意</span>
            <button
              v-if="child.refineOpen && (child.seconds !== null || child.scroll !== null)"
              class="clear-btn"
              type="button"
              @click="condActions.clearRefine(child.id)"
            >クリア</button>
          </div>
          <div v-if="child.refineOpen" class="refinement-controls">
            <div class="query-params-note">以下のすべてを満たす</div>
            <div class="refine-items">
              <div class="refine-item">
                <div class="refine-label"><span class="icon">⏱</span>画面滞在</div>
                <div class="plus-minus">
                  <button class="pm-btn" type="button" @click="condActions.refineAdjust(child.id, 'seconds', -5, 10)">−</button>
                  <input
                    class="pm-input"
                    type="text"
                    readonly
                    :value="child.seconds != null ? child.seconds : ''"
                  />
                  <button class="pm-btn" type="button" @click="condActions.refineAdjust(child.id, 'seconds', 5, 10)">+</button>
                </div>
                <span class="refine-unit">秒</span>
              </div>
              <div class="refine-item">
                <div class="refine-label"><span class="icon">⇵</span>スクロール</div>
                <div class="plus-minus">
                  <button class="pm-btn" type="button" @click="condActions.refineAdjust(child.id, 'scroll', -5, 50)">−</button>
                  <input
                    class="pm-input"
                    type="text"
                    readonly
                    :value="child.scroll != null ? child.scroll : ''"
                  />
                  <button class="pm-btn" type="button" @click="condActions.refineAdjust(child.id, 'scroll', 5, 50)">+</button>
                </div>
                <span class="refine-unit">%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 追加ボタン群 -->
    <!-- v1 本リリースでは PagesGroup 配下のネストグループを禁止しているため、「条件のグループを追加」ボタンは出さない。 -->
    <div class="add-row" :class="{ 'nested': !isRoot }">
      <button
        class="add-btn-main"
        type="button"
        @click="condActions.addPattern(group.id)"
      >
        <span class="circle-plus">
          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
            <use xlink:href="/assets/img/sprite.svg#plus" />
          </svg>
        </span>
        {{ contextRoot === 'unschedule' ? '解除するページの追加' : contextRoot === 'suppress' ? '除外するページの追加' : '予約するページの追加' }}
      </button>
    </div>
  </div>
</template>

<script>
// 条件グループ再帰描画コンポーネント。
// ルートschedule/unschedule/suppressグループおよび任意階層のネストグループを同一テンプレートで表現する。
// 親ページが provide する condActions を inject して各種変更処理を呼び出す。
export default {
  name: 'ConditionGroupNode',
  inject: ['condActions', 'condRoot'],
  props: {
    group: { type: Object, required: true },
    isRoot: { type: Boolean, default: false },
    contextRoot: { type: String, default: 'schedule' },
  },
  data: function() {
    return {
      // URL 条件は「等しい」「含む」の 2 種に絞り込み、デフォルトは eq。
      // 「等しくない」「含まない」は当面 UI から除外する (レガシー JSON 値は読込時に eq へコアースされる)。
      urlMethodOptions: [
        { value: 'eq', label: '等しい' },
        { value: 'contains', label: '含む' },
      ],
      // QueryString 側は 4 種すべて選択可能。表示順は 等しい / 等しくない / 含む / 含まない。
      queryMethodOptions: [
        { value: 'eq', label: '等しい' },
        { value: 'notEq', label: '等しくない' },
        { value: 'contains', label: '含む' },
        { value: 'notContains', label: '含まない' },
      ],
      // URL/QueryString 統合の元定義。復活時は上記 2 定義を消してこれを再利用する。
      /*
            methodOptions: [
                { value: 'contains', label: '含む' },
                { value: 'notContains', label: '含まない' },
                { value: 'eq', label: '等しい' },
                { value: 'notEq', label: '等しくない' },
            ],
            */
    };
  },
  computed: {
    // SelectText コンポーネントの elements プロパティ向けに
    // { value: label } の連想配列へ変換する。
    methodElements: function() {
      var obj = {};
      this.urlMethodOptions.forEach(function(o) { obj[o.value] = o.label; });
      return obj;
    },
  },
  methods: {
    methodLabel: function(method) {
      // URL / QueryString 双方の候補から検索。レガシーな notEq/notContains も表示できるようにする。
      var all = this.urlMethodOptions.concat(this.queryMethodOptions);
      var found = all.find(function(o) { return o.value === method; });
      return found ? found.label : method;
    },
    urlDisplay: function(pattern) {
      return (pattern.url && pattern.url.trim()) || '/cart';
    },
    urlTail: function(pattern) {
      return this.urlDisplay(pattern).replace(/^\//, '');
    },
    // 親ページの errors マップから指定 nodeId / field のメッセージを取得するヘルパ。
    // condRoot.errorOf() を呼ぶことで親側の reactivity を維持したまま参照できる。
    errorOf: function(nodeId, field) {
      if (!this.condRoot || typeof this.condRoot.errorOf !== 'function') return null;
      return this.condRoot.errorOf(nodeId, field);
    },
  },
}
</script>

<style>
/* c-error--balloon は position:absolute のため、入力ごとに相対基点となるラッパを挟む */
.cond-group-root .input-wrap {
    position: relative;
    display: inline-block;
    flex: 1;
    min-width: 0;
}
.cond-group-root .query-param-row .input-wrap {
    flex: 1;
}
.cond-group-root .input-wrap .input-field {
    width: 100%;
}
.cond-group-root .input-wrap.has-error .input-field,
.cond-group-root .refine-item.has-error .pm-input {
    border-color: #d9534f;
    background: #fff6f6;
}
/* refine-item は flex 配置、balloon は右上アンカーで余白を確保 */
.cond-group-root .refine-item {
    position: relative;
}
.cond-group-root .refine-item .c-error--balloon {
    top: -20px;
    right: 0;
}
</style>

<!-- refine-item のバリデーションエラー吹き出しは、入力欄の上に十分な逃げ(-50px)を設けて
     プレースホルダや隣接要素と重ならないように本コンポーネントスコープで個別調整する。 -->
<style scoped>
.cond-group-root .refine-item .c-error--balloon {
    top: -50px;
}
</style>
